import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// Safe formatter for tooltips
export const safeFormatter = (value, name) => {
    if (value === null || value === undefined || typeof value !== 'number' || isNaN(value)) {
        return [value, name];
    }
    return [value.toFixed(3), name];
};

// Daily Averages Chart Component
export const DailyAveragesChart = ({ data }) => {
    if (!data || data.length === 0) {
        return null;
    }

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h4 className="font-semibold text-gray-800 mb-4">Daily Averages</h4>
            
            <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                            dataKey="date" 
                            tick={{ fontSize: 12 }}
                            angle={-45}
                            textAnchor="end"
                            height={80}
                        />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip 
                            formatter={safeFormatter}
                            labelFormatter={(label) => `Date: ${label}`}
                        />
                        <Legend />
                        
                        {/* Pitch DTW Line */}
                        <Line 
                            type="monotone" 
                            dataKey="DTW" 
                            stroke="#ff6b6b" 
                            strokeWidth={2}
                            dot={{ fill: "#ff6b6b", r: 4, strokeWidth: 1, stroke: "#fff" }}
                            name="Pitch DTW (Daily Avg)"
                        />
                        
                        {/* Correlation Line */}
                        <Line 
                            type="monotone" 
                            dataKey="correlation" 
                            stroke="#9d4edd" 
                            strokeWidth={2}
                            dot={{ fill: "#9d4edd", r: 4, strokeWidth: 1, stroke: "#fff" }}
                            name="Pitch Correlation (Daily Avg)"
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
            
            <div className="mt-4 text-sm text-gray-600">
                <p><strong>Daily averages</strong> - Shows the average performance for each day with connected lines.</p>
                <div className="grid grid-cols-2 gap-4 mt-2 text-xs">
                    <div>
                        <strong>Interpretation:</strong>
                        <ul className="list-disc list-inside ml-2">
                            <li>DTW: Lower is better</li>
                            <li>Correlation: Higher is better</li>
                        </ul>
                    </div>
                    <div>
                        <strong>Data Points:</strong>
                        <ul className="list-disc list-inside ml-2">
                            <li>{data.length} days analyzed</li>
                            <li>{data.reduce((sum, day) => sum + day.count, 0)} total recordings</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Trend Chart Component
export const TrendChart = ({ data, trendView, trendAlgorithm }) => {
    if (!data || data.length === 0) {
        return null;
    }

    return (
        <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 12 }}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip 
                        formatter={safeFormatter}
                        labelFormatter={(label, payload) => {
                            if (payload && payload[0] && payload[0].payload) {
                                const point = payload[0].payload;
                                return `Recording: ${point.date} (${trendAlgorithm} trend)`;
                            }
                            return label;
                        }}
                    />
                    <Legend />
                    
                    {/* DTW Lines */}
                    {(trendView === 'dtw' || trendView === 'both') && (
                        <>
                            <Line 
                                type="monotone" 
                                dataKey="DTW" 
                                stroke="#ff6b6b" 
                                strokeWidth={0}
                                dot={{ fill: "#ff6b6b", r: 2, strokeWidth: 1, stroke: "#fff" }}
                                name="Pitch DTW"
                            />
                            
                            <Line 
                                type="monotone" 
                                dataKey="DTWTrend" 
                                stroke="#ff6b6b" 
                                strokeWidth={2}
                                dot={false}
                                name={`Pitch DTW Trend (${trendAlgorithm})`}
                            />
                            
                            <Line 
                                type="monotone" 
                                dataKey="intensity_DTW" 
                                stroke="#4ecdc4" 
                                strokeWidth={0}
                                dot={{ fill: "#4ecdc4", r: 2, strokeWidth: 1, stroke: "#fff" }}
                                name="Intensity DTW"
                            />
                            
                            <Line 
                                type="monotone" 
                                dataKey="intensity_DTWTrend" 
                                stroke="#4ecdc4" 
                                strokeWidth={2}
                                dot={false}
                                name={`Intensity DTW Trend (${trendAlgorithm})`}
                            />
                        </>
                    )}
                    
                    {/* Correlation Lines */}
                    {(trendView === 'correlation' || trendView === 'both') && (
                        <>
                            <Line 
                                type="monotone" 
                                dataKey="correlation" 
                                stroke="#9d4edd" 
                                strokeWidth={0}
                                dot={{ fill: "#9d4edd", r: 2, strokeWidth: 1, stroke: "#fff" }}
                                name="Pitch Correlation"
                            />
                            
                            <Line 
                                type="monotone" 
                                dataKey="correlationTrend" 
                                stroke="#9d4edd" 
                                strokeWidth={2}
                                dot={false}
                                name={`Pitch Correlation Trend (${trendAlgorithm})`}
                            />
                            
                            <Line 
                                type="monotone" 
                                dataKey="intensity_correlation" 
                                stroke="#f48c06" 
                                strokeWidth={0}
                                dot={{ fill: "#f48c06", r: 2, strokeWidth: 1, stroke: "#fff" }}
                                name="Intensity Correlation"
                            />
                            
                            <Line 
                                type="monotone" 
                                dataKey="intensity_correlationTrend" 
                                stroke="#f48c06" 
                                strokeWidth={2}
                                dot={false}
                                name={`Intensity Correlation Trend (${trendAlgorithm})`}
                            />
                        </>
                    )}
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};